import { createSession, finishSession, postAttempts, voidAttempt } from '#shared/api/drills';
import type { RouteInput } from '#shared/core/contract';
import type { EventBatchRes } from '#shared/core/event-batch';
import { uuidv7 } from '#shared/core/id';
import type { SpotKey } from '#shared/core/spots';
import { buildTurns, type IsoMatch, type RotationMode } from '#shared/domain/drills/index';
import { coverageFrom } from '#shared/domain/scoring/index';
import type { CombineLevel, DrillAttempt, DrillShotClass } from '#shared/schemas/drill';
import { RULE_PRESETS } from '#shared/schemas/rules';
import { fill, plural } from '@/copy';
import { api, isApiError } from '@/lib/api';
import { enqueue, enqueueCreate, outbox, registerOutboxStream } from '@/lib/outbox';
import { queryClient } from '@/lib/query';
import { serverNow } from '@/lib/signals';
import { toast } from '@/lib/toast';
import { startGame } from '../games/public';
import { flushPendingGuests, PLAYERS_KEY } from '../players/public';
import { STATS_KEY } from '../stats/public';
import { copy } from './copy';
import { COMBINE_KEY, combineKeys } from './queries';
import { type IsoGameRef, type LocalSession, useCombineStore, writeLastShooter } from './store';

/**
 * Where the recorder's phone meets the outbox and the server. One drill session is one outbox
 * stream (kind 'drill'): the create goes first (`enqueueCreate`, always before the first shot, even
 * online), then one row per shot. Every shot is also kept in the combine store, because the
 * outbox is a transit queue and the screen must survive a reload with no signal.
 *
 *  - Undo takes the last write back. A shot still in the outbox is simply dropped from it. A shot
 *    the server already has is voided with the void route, and when that call cannot go out it
 *    waits in `pendingVoids` for the next signal. The void is its own call, so the stream's next
 *    batch waits for the session's voids first (`settleVoids`, the stream's `beforeBatch`): the
 *    server counts live shots against the level cap, and a replacement shot that got there before
 *    the void would be refused.
 *  - Finish is a plain POST. It goes up after the outbox flush, and when there is no signal (or the
 *    create has not landed) it stays queued and is replayed on `online`, when the app comes back to
 *    the front and every 15 s. A finish that beats its last shots is fine: late shots are accepted
 *    and the server publishes `drill.session.corrected` for them.
 */

type CreateBody = RouteInput<typeof createSession>['body'];

export interface Me {
  memberId: string;
  deviceId: string;
}

const phoneOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

function refresh(sessionId?: string): void {
  void queryClient.invalidateQueries({ queryKey: sessionId ? combineKeys.session(sessionId) : COMBINE_KEY });
}

/** Everything that reads ratings or the core combine flag has changed. */
function refreshRatings(): void {
  void queryClient.invalidateQueries({ queryKey: COMBINE_KEY });
  // The hub's status query is not mounted on the results screen. Marking it stale is not enough: the
  // persisted copy comes back as fresh after a reload, so refetch it now.
  void queryClient.invalidateQueries({ queryKey: ['combine', 'status'], refetchType: 'all' });
  void queryClient.invalidateQueries({ queryKey: STATS_KEY });
  void queryClient.invalidateQueries({ queryKey: PLAYERS_KEY });
}

// ---------------------------------------------------------------- start

export interface StartInput {
  level: CombineLevel;
  playerIds: string[];
  mode: RotationMode;
  entry: 'tap' | 'fast';
  /** Iso Gauntlet pairings, from isoSchedule. */
  iso?: IsoMatch[];
  /** undefined: auto. null: detached. */
  sessionId?: string | null;
}

/** Make the drill on this phone first (the recorder works with no signal) and queue its create. */
export async function startSession(i: StartInput): Promise<string> {
  const id = uuidv7();
  const now = serverNow();
  const turns = i.level === 'iso' ? [] : buildTurns(i.level, i.playerIds, i.mode);
  const session: LocalSession = {
    id,
    level: i.level,
    playerIds: i.playerIds,
    turns,
    mode: i.mode,
    entry: i.entry,
    startedAt: now,
    ...(i.iso ? { iso: { matches: i.iso, games: [] } } : {}),
  };
  const body: CreateBody = {
    id,
    program: 'combine_v1',
    level: i.level,
    playerIds: i.playerIds,
    startedAt: now,
    clientTs: now,
    ...(i.sessionId !== undefined ? { sessionId: i.sessionId } : {}),
  };
  await useCombineStore.getState().start(session);
  // A guest added on this phone has to reach the server before a create that names them.
  await flushPendingGuests();
  await enqueueCreate({ kind: 'drill', id }, { url: api.url(createSession), body });
  return id;
}

// ---------------------------------------------------------------- shots

export interface ShotInput {
  playerId: string;
  spot: SpotKey;
  shotClass: DrillShotClass;
  made: boolean;
}

/** Write shots to this phone and the outbox. One call is one undo step. */
export function writeShots(sessionId: string, shots: readonly ShotInput[], me: Me): DrillAttempt[] {
  const store = useCombineStore.getState();
  const slot = store.slots[sessionId];
  const session = slot?.session;
  if (!slot || !session || shots.length === 0) return [];
  let seq = slot.seq;
  const attempts: DrillAttempt[] = shots.map((s) => ({
    id: uuidv7(),
    drillSessionId: sessionId,
    playerId: s.playerId,
    level: session.level,
    spot: s.spot,
    shotClass: s.shotClass,
    made: s.made,
    seq: ++seq,
    deviceId: me.deviceId,
    clientTs: serverNow(),
    authorMemberId: me.memberId,
  }));
  store.addAttempts(sessionId, attempts, seq);
  for (const a of attempts) void enqueue({ kind: 'drill', id: sessionId }, a);
  const last = attempts[attempts.length - 1];
  if (last) writeLastShooter(last.playerId, last.clientTs);
  return attempts;
}

/** Take back the last write (a shot, or a whole fast-entry set). */
export async function undoLast(sessionId: string): Promise<DrillAttempt[]> {
  const store = useCombineStore.getState();
  const synced = new Set(store.slots[sessionId]?.synced ?? []);
  const removed = store.undoLast(sessionId);
  const local = removed.filter((a) => !synced.has(a.id)).map((a) => a.id);
  if (local.length) await outbox.dismiss(local);
  for (const a of removed) if (synced.has(a.id)) void voidRemote(sessionId, a.id);
  return removed;
}

/** Void calls in flight, per session, so the next batch of shots can wait for them. */
const voiding = new Map<string, Set<Promise<void>>>();

/** Void a shot the server has. Waits for signal when the call cannot go out. */
export function voidRemote(sessionId: string, attemptId: string): Promise<void> {
  const p = sendVoid(sessionId, attemptId);
  const set = voiding.get(sessionId) ?? new Set<Promise<void>>();
  voiding.set(sessionId, set);
  set.add(p);
  const done = () => {
    set.delete(p);
    if (set.size === 0 && voiding.get(sessionId) === set) voiding.delete(sessionId);
  };
  void p.then(done, done);
  return p;
}

/**
 * Before a batch of shots goes up: let this session's voids reach the server, the ones in flight and
 * the ones that waited for signal. Resolves once they have been tried; a void that cannot go out
 * stays in `pendingVoids` and the batch goes up anyway (the outbox bounds the wait).
 */
export async function settleVoids(sessionId: string): Promise<void> {
  if (!phoneOnline()) return;
  const queued = useCombineStore.getState().slots[sessionId]?.pendingVoids ?? [];
  for (const v of queued) void voidRemote(sessionId, v);
  const inflight = voiding.get(sessionId);
  if (inflight && inflight.size > 0) await Promise.allSettled([...inflight]);
}

async function sendVoid(sessionId: string, attemptId: string): Promise<void> {
  const store = useCombineStore.getState();
  try {
    await api.call(voidAttempt, { params: { id: attemptId } });
    store.removeVoid(sessionId, attemptId);
    store.forgetUndone(sessionId, [attemptId]);
    refresh(sessionId);
  } catch (e) {
    if (isApiError(e) && e.status === 404) {
      // The server never had it (the shot was dropped before it went up).
      store.removeVoid(sessionId, attemptId);
      store.forgetUndone(sessionId, [attemptId]);
      return;
    }
    store.addVoid(sessionId, attemptId);
  }
}

/** Reads one batch answer: remembers what the server has, voids shots taken back in flight, drops refusals. */
export function onBatch(sessionId: string, res: EventBatchRes): void {
  const store = useCombineStore.getState();
  const had = [...res.accepted.map((a) => a.id), ...res.duplicates];
  store.markSynced(sessionId, had);
  const undone = new Set(store.slots[sessionId]?.undone ?? []);
  for (const id of had) if (undone.has(id)) void voidRemote(sessionId, id);
  if (res.rejected.length) {
    const ids = res.rejected.map((r) => r.id);
    store.dropAttempts(sessionId, ids);
    store.addRejected(sessionId, ids.length);
    toast({
      title: copy.rec.parked,
      body: fill(copy.rec.parkedBody, { n: ids.length }),
      tone: 'warning',
      duration: 8000,
    });
    void outbox.dismiss(ids);
  }
  if (res.accepted.length || res.duplicates.length || res.rejected.length) refresh(sessionId);
  void resumeQueued();
}

// ---------------------------------------------------------------- finish

/** Finish or bin the run. Safe on this phone at once; the server hears about it when it can. */
export async function finishRun(sessionId: string, status: 'final' | 'abandoned' = 'final'): Promise<void> {
  const store = useCombineStore.getState();
  if (store.slots[sessionId]?.finish) return;
  store.setFinish(sessionId, { status, endedAt: serverNow(), state: 'queued' });
  await tryFinish(sessionId);
}

const finishing = new Set<string>();

/** One try at the finish call. Returns true when the server has it. */
export async function tryFinish(sessionId: string): Promise<boolean> {
  const store = useCombineStore.getState();
  const f = store.slots[sessionId]?.finish;
  if (!f || f.state === 'done' || finishing.has(sessionId) || !phoneOnline()) return f?.state === 'done';
  finishing.add(sessionId);
  try {
    await outbox.flush();
    // A create the server refused can never be finished. The recorder shows the parked plays.
    if ((await outbox.rejected({ kind: 'drill', id: sessionId })).some((r) => r.op === 'create')) return false;
    await api.call(finishSession, { params: { id: sessionId }, body: { endedAt: f.endedAt, status: f.status } });
    useCombineStore.getState().setFinish(sessionId, { ...f, state: 'done' });
    refreshRatings();
    return true;
  } catch {
    // No signal, or the create has not landed (404): stays queued and is tried again.
    return false;
  } finally {
    finishing.delete(sessionId);
  }
}

/** Replays whatever waited for signal: finishes and voids. Safe to call often. */
export async function resumeQueued(): Promise<void> {
  if (!phoneOnline()) return;
  const store = useCombineStore.getState();
  await store.loadIndex();
  const { slots, index } = useCombineStore.getState();
  for (const e of index) {
    const s = slots[e.id];
    if (!s) continue;
    for (const v of s.pendingVoids) await voidRemote(e.id, v);
    if (s.finish?.state === 'queued') await tryFinish(e.id);
  }
}

// ---------------------------------------------------------------- Iso Gauntlet

export interface IsoStartInput {
  drillId: string;
  a: string;
  b: string;
  round: number;
  /** The run the Gauntlet belongs to, so its games land in the same run. undefined: auto. */
  runId?: string | null;
  me: Me;
}

/**
 * Start one Iso Gauntlet game: a real 1v1 to 3 in honor mode, made by games' `startGame` with
 * `drillSessionId`, so it counts as a 1v1 for Elo and as a Gauntlet game for the combine. It works
 * with no signal like any other game. The Gauntlet's own create is the first row of its outbox
 * stream, so it goes up before this game's create; we flush once first so it lands before the game
 * names it (the server drops a drill id it has never seen).
 */
export async function startIsoGame(i: IsoStartInput): Promise<string> {
  await outbox.flush();
  const { id } = await startGame({
    mode: '1v1',
    rules: RULE_PRESETS.iso3,
    coverage: coverageFrom({}),
    honor: true,
    players: [
      { playerId: i.a, team: 'A', starter: true },
      { playerId: i.b, team: 'B', starter: true },
    ],
    keeperMemberId: null,
    ...(i.runId !== undefined ? { sessionId: i.runId } : {}),
    drillSessionId: i.drillId,
    me: i.me,
  });
  const store = useCombineStore.getState();
  const games = store.slots[i.drillId]?.session?.iso?.games ?? [];
  const ref: IsoGameRef = { gameId: id, a: i.a, b: i.b, round: i.round };
  store.setIsoGames(i.drillId, [...games, ref]);
  refresh(i.drillId);
  return id;
}

// ---------------------------------------------------------------- init

/** The outbox stream and the listeners that replay queued finishes. Called once from the module's init(). */
export function registerDrillStream(): () => void {
  registerOutboxStream('drill', {
    url: (id) => api.url(postAttempts, { params: { id } }),
    body: (items) => ({ attempts: items }),
    onResult: onBatch,
    onCreated: () => void resumeQueued(),
    beforeBatch: settleVoids,
  });
  if (typeof window === 'undefined') return () => {};
  const wake = () => void resumeQueued();
  const onVisible = () => {
    if (document.visibilityState === 'visible') wake();
  };
  window.addEventListener('online', wake);
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(wake, 15_000);
  wake();
  return () => {
    window.removeEventListener('online', wake);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}

export const waitingText = (n: number): string => fill(copy.rec.waiting, { n });
export const shotsText = (n: number): string => plural(n, 'shot', 'shots');
